"use client";

import { memo } from "react";
import { useSmartMoney } from "@/hooks/useSmartMoney";
import { useSmartActivityMap, type SmartActivityRow } from "@/hooks/useSmartActivityMap";
// WhaleIcon removed 2026-05-10 — the leading 🐋 was visually redundant
// next to the "SMART $" / "SMART MONEY" label that already names the
// concept. Saves four icon mounts per card on the markets list (×30
// cards = 120 icon trees).
import Tooltip from "./Tooltip";
import { useT } from "@/hooks/useT";

// Predexon outage: hide the smart-money badge on cards / trading panels under
// the flag (the underlying data source is down).
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

interface MarketRef {
  conditionId: string;
  title?: string;
}

interface Props {
  conditionId: string | undefined | null;
  /**
   * For multi-market events (e.g. "Iran by Jan / Feb / Mar"), pass the list
   * of nested markets. The badge picks the market with the highest smart
   * wallet count as the "leader" and labels which outcome it refers to.
   * When omitted or length ≤ 1, falls back to `conditionId` alone.
   */
  markets?: MarketRef[];
  /**
   * card   → row shown below the card footer with wallet count + % + volume
   * inline → compact row used in trading panels (keeps per-card fetch for freshness)
   */
  variant?: "card" | "inline";
  /** Hide entirely if smart wallets total < this threshold (default 5) */
  minWallets?: number;
  className?: string;
}

function formatVolume(v: number | null | undefined): string {
  const n = v ?? 0;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

/**
 * Compact Smart Money badge.
 * - `card` variant uses the BULK `useSmartActivityMap()` hook so N cards share
 *   a single HTTP request (O(1) lookup per card).
 * - `inline` variant still uses per-card `useSmartMoney()` because trading
 *   panels need the richer net_positioning number and freshest data.
 */
function SmartMoneyBadgeImpl({ conditionId, markets, variant = "card", minWallets = 5, className = "" }: Props) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const t = useT();
  // ─── card variant ───────────────────────────────────────────────
  // Reads from the shared smart-activity map. No per-card fetch.
  if (variant === "card") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: map } = useSmartActivityMap();

    // Predexon outage: hide entirely (after hooks to respect rules of hooks).
    if (HIDE_PREDEXON) return null;

    // Pick the leader market: if a `markets` list was provided, find the
    // one with the highest smart_wallet_count in the activity map. Otherwise
    // fall back to the single `conditionId`.
    let leaderRow: SmartActivityRow | undefined;
    let leaderLabel: string | undefined;
    if (markets && markets.length > 0 && map) {
      for (const m of markets) {
        const cid = (m.conditionId || "").toLowerCase();
        if (!cid) continue;
        const candidate = map.get(cid);
        if (!candidate) continue;
        if (!leaderRow || candidate.smartWalletCount > leaderRow.smartWalletCount) {
          leaderRow = candidate;
          leaderLabel = m.title;
        }
      }
    } else {
      const cid = (conditionId || "").toLowerCase();
      leaderRow = cid && map ? map.get(cid) : undefined;
    }
    const isMulti = !!(markets && markets.length > 1);
    if (!leaderRow || leaderRow.smartWalletCount < minWallets) {
      // Placeholder row — mirrors the populated layout (count · side · vol ·
      // ROI) so the GRID cards line up visually. "N/A" in each slot instead
      // of a single collapsed dash, with the same dividers in between.
      return (
        <Tooltip content={t("tooltip.smartMoneyEarly")} maxWidth={240} className="w-full">
        <div className={`flex items-center justify-between gap-2 px-2 py-1 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-[#1A1A1A] w-full overflow-hidden ${className}`}>
          <span className="flex items-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
            <span>{t("smartMoney.shortLabel")}</span>
          </span>
          <span className="flex items-center gap-2 text-[10px] tabular-nums flex-shrink min-w-0 whitespace-nowrap overflow-hidden text-gray-400 dark:text-gray-600">
            <span className="font-semibold">N/A</span>
            <span className="w-px h-3 bg-gray-300 dark:bg-white/10" />
            <span className="font-black">N/A</span>
            <span className="w-px h-3 bg-gray-300 dark:bg-white/10" />
            <span className="font-medium">N/A</span>
          </span>
        </div>
        </Tooltip>
      );
    }

    const yesPct = Math.round(leaderRow.netBuyersPct * 100);
    const isYesBias = yesPct >= 50;
    const dominantPct = isYesBias ? yesPct : 100 - yesPct;
    const dominantSide = isYesBias ? "YES" : "NO";
    const accent = isYesBias ? "text-[#00FF9D]" : "text-red-400";

    const tooltipText = [
      isMulti && leaderLabel ? t("smartMoney.outcome", { label: leaderLabel }) : null,
      t("smartMoney.wallets", { count: leaderRow.smartWalletCount }),
      t("smartMoney.netPositioning", { pct: dominantPct, side: dominantSide }),
      t("smartMoney.volume", { volume: formatVolume(leaderRow.smartVolume) }),
      Number.isFinite(leaderRow.avgSmartRoi)
        ? t("smartMoney.avgRoi", { roi: `${leaderRow.avgSmartRoi >= 0 ? '+' : ''}${(leaderRow.avgSmartRoi * 100).toFixed(1)}%` })
        : null,
      Number.isFinite(leaderRow.avgSmartWinRate) && leaderRow.avgSmartWinRate > 0
        ? t("smartMoney.avgWinRate", { rate: `${(leaderRow.avgSmartWinRate * 100).toFixed(0)}%` })
        : null,
    ].filter(Boolean).join('\n');

    return (
      <Tooltip content={tooltipText} maxWidth={240} className="w-full">
      <div
        className={`flex items-center justify-between gap-2 px-2 py-1 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-[#1A1A1A] w-full overflow-hidden ${className}`}
      >
        <span className="flex items-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
          <span>{t("smartMoney.shortLabel")}</span>
        </span>
        <span className="flex items-center gap-2 text-[10px] tabular-nums flex-shrink min-w-0 whitespace-nowrap overflow-hidden">
          <span className="font-semibold text-gray-600 dark:text-gray-400">{leaderRow.smartWalletCount}</span>
          <span className="w-px h-3 bg-gray-300 dark:bg-white/10" />
          <span className={`font-black ${accent}`}>{dominantPct}% {dominantSide}</span>
          <span className="w-px h-3 bg-gray-300 dark:bg-white/10" />
          <span className="font-medium text-gray-500 dark:text-gray-500">{formatVolume(leaderRow.smartVolume)}</span>
          {Number.isFinite(leaderRow.avgSmartRoi) && leaderRow.avgSmartRoi !== 0 && (
            <>
              <span className="w-px h-3 bg-gray-300 dark:bg-white/10" />
              <span className={`font-black ${leaderRow.avgSmartRoi >= 0 ? 'text-[#00FF9D]' : 'text-red-400'}`}>
                {leaderRow.avgSmartRoi >= 0 ? '+' : ''}{(leaderRow.avgSmartRoi * 100).toFixed(0)}% ROI
              </span>
            </>
          )}
        </span>
      </div>
      </Tooltip>
    );
  }

  // ─── inline variant (trading panel) ─────────────────────────────
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, isLoading } = useSmartMoney(conditionId);

  // Predexon outage: hide entirely (after hooks to respect rules of hooks).
  if (HIDE_PREDEXON) return null;

  // Show a skeleton while the first fetch is in flight so the trading panel
  // doesn't reflow when the badge mounts. Same dimensions as the real badge.
  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-[#1A1A1A] animate-pulse ${className}`}
      >
        <span className="flex items-center">
          <span className="h-3 w-20 rounded bg-gray-200 dark:bg-white/10" />
        </span>
        <span className="h-3 w-16 rounded bg-gray-200 dark:bg-white/10" />
      </div>
    );
  }

  if (!data) return null;
  const totalWallets = data.smart_wallets_yes + data.smart_wallets_no;
  if (!Number.isFinite(totalWallets) || totalWallets < minWallets || totalWallets <= 0) return null;

  const yesPercent = Math.round((data.smart_wallets_yes / totalWallets) * 100);
  const isYesBias = data.net_positioning >= 0;
  const dominantSide = yesPercent >= 50 ? "YES" : "NO";
  const dominantPct = yesPercent >= 50 ? yesPercent : 100 - yesPercent;
  if (!Number.isFinite(yesPercent) || !Number.isFinite(dominantPct)) return null;
  const accent = isYesBias ? "text-[#00FF9D]" : "text-red-400";

  return (
    <div
      className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-[#1A1A1A] ${className}`}
      title={t("smartMoney.summaryYesNo", {
        yes: data.smart_wallets_yes,
        no: data.smart_wallets_no,
        volume: formatVolume(data.total_smart_volume),
      })}
    >
      <span className="flex items-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        <span>{t("smartMoney.label")}</span>
      </span>
      <span className={`text-[11px] font-black tabular-nums ${accent}`}>
        {dominantPct}% {dominantSide}
      </span>
    </div>
  );
}

// Memo with a comparator that ignores price-tick updates. The parent
// EventCard rebuilds the `markets` array on every render
// (`displayMarkets.filter(...).map(...)`), so default Object.is would
// never skip — even though smart-wallet stats only depend on
// conditionId + label and never on outcomePrices. Comparing length +
// per-market conditionId/title catches the no-op case.
function smartMoneyBadgePropsEqual(prev: Props, next: Props): boolean {
  if (prev.conditionId !== next.conditionId) return false;
  if (prev.variant !== next.variant) return false;
  if (prev.minWallets !== next.minWallets) return false;
  if (prev.className !== next.className) return false;
  const a = prev.markets;
  const b = next.markets;
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].conditionId !== b[i].conditionId) return false;
    if (a[i].title !== b[i].title) return false;
  }
  return true;
}

const SmartMoneyBadge = memo(SmartMoneyBadgeImpl, smartMoneyBadgePropsEqual);
SmartMoneyBadge.displayName = "SmartMoneyBadge";

export default SmartMoneyBadge;
